// eslint-disable-next-line no-redeclare, @typescript-eslint/no-redeclare
import {
  ActionIcon,
  Avatar,
  Box,
  Checkbox,
  Group,
  MultiSelect,
  SelectItemProps,
  Space,
  Stack,
  Text as MantineText,
  Tooltip,
} from '@mantine/core';
import { i18n } from '@lingui/core';
import { FC, forwardRef, useContext, useEffect, useMemo, useState } from 'react';
import { IconUser, IconX } from '@tabler/icons';
import { DateRangePickerWithSelect } from '../../Atoms/inputs/DateRangePickerWithSelect';
import { openDeleteConfirmModal } from '../../Atoms/DeleteConfirmModal';
import AddTaskIcon from '../../Organismns/Tasks/AddTaskIcon';
import { TaskListContext } from '../../Service/Context/TaskListContext';
import { AgendaS } from '../../Service/restapi/taskService';
import { WindowContext } from '../../Service/Context/WindowContext';
import { useBookitupScope, useBookitupSubscription } from '../../Service/Context/BookitupSubscriptionContext';
import { useAssignableApplicationUsers } from '../../Service/hooks/useAssignableApplicationUsers';
import { useDateRangeItem } from '../../Service/hooks/useDateRangeItem';
import { ProfileContext } from '../../Service/Context/ProfileContext';
import AgendaGroupEntityType from '../../Types/Enums/AgendaGroupEntityType';
import { addDays, lastDateBeforeNextMidnight, lastMidnight } from '../../Utils/dateUtils';
import BookingStateItem from '../EventList/BookingStateItem';
import { ThemeText, ThemeTitle } from '../../Atoms/ThemeText';

interface ApplicationUserFilterItemProps extends SelectItemProps {
  email: string;
  firstName?: string;
  lastName?: string;
}

type ApplicationUserLabelData = {
  email: string;
  firstName?: string;
  lastName?: string;
};

const getApplicationUserInitials = ({ firstName, lastName, email }: ApplicationUserLabelData) => {
  const initials = `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`;
  return initials || email.charAt(0).toUpperCase();
};

const ApplicationUserFilterItem = forwardRef<HTMLDivElement, ApplicationUserFilterItemProps>(
  ({ label, email, firstName, lastName, ...rest }: ApplicationUserFilterItemProps, ref) => (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <div ref={ref} {...rest}>
      <Group noWrap spacing="sm">
        <Avatar size={30} radius="xl">
          {getApplicationUserInitials({ firstName, lastName, email })}
        </Avatar>
        <Stack spacing={0}>
          <MantineText size="sm" weight={500}>
            {label}
          </MantineText>
          {email && label !== email && (
            <MantineText size="xs" color="dimmed">
              {email}
            </MantineText>
          )}
        </Stack>
      </Group>
    </div>
  ),
);

const TasksNavFilter: FC<{ closeDrawer?: () => unknown }> = () => {
  const { taskListFilter, taskListContent, changeFilter, loadGroups } = useContext(TaskListContext);
  const { isScopeSelected: hasGeneralTasksScope } = useBookitupScope('GENERAL_TASKS');
  const { teamAddonPresent } = useBookitupSubscription();
  const { selectedTaskGroups, selectedDateRange, includeCompleted, applicationUsersOfInterests } = taskListFilter;
  const { taskGroups } = taskListContent;
  const allGroupsSelected = selectedTaskGroups.length === taskGroups.length;
  const groupSections = [
    {
      label: 'Allgemein',
      groups: taskGroups.filter((group) => group.entityType === AgendaGroupEntityType.profiles),
    },
    {
      label: 'Events',
      groups: taskGroups.filter((group) => group.entityType === AgendaGroupEntityType.events),
    },
  ];
  const { bookitupTeam } = useContext(ProfileContext);
  const [groupItemCounts, setGroupItemCounts] = useState<Record<string, number>>({});
  const [deletingGroupId, setDeletingGroupId] = useState<string>();
  const loadingApplicationUsers = bookitupTeam.loading && !bookitupTeam.sharesLoaded;
  const applicationUserItems = useAssignableApplicationUsers(bookitupTeam);
  const dateRangePickerMinimum = new Date(2020, 1, 1);
  const dateRangePickerMaximum = new Date(new Date().getFullYear() + 10, 1, 1);
  const startOfDay = lastMidnight();
  const { sm } = useContext(WindowContext);

  const onDateChange = (values: [Date | null, Date | null]) => {
    if (values[0] && values[1]) {
      changeFilter({ selectedDateRange: [values[0], values[1]] });
    }
  };

  useEffect(() => {
    if (!hasGeneralTasksScope || !teamAddonPresent) {
      return;
    }

    bookitupTeam.ensureSharesLoaded().catch(() => undefined);
  }, [bookitupTeam, hasGeneralTasksScope, teamAddonPresent]);

  useEffect(() => {
    let mounted = true;

    AgendaS.fetchAgendaGroupsWithCounts().then((groups) => {
      if (mounted) {
        setGroupItemCounts(
          groups.reduce<Record<string, number>>((counts, group) => ({ ...counts, [group.id]: group.itemCounts }), {}),
        );
      }
    });

    return () => {
      mounted = false;
    };
  }, [taskGroups]);

  const onIncludeCompletedChange = (checked: boolean) => {
    changeFilter({ includeCompleted: checked });
  };

  const selectAllGroups = () => {
    changeFilter({ selectedTaskGroups: [...taskGroups], page: 0 });
  };

  const toggleGroup = (groupId: string) => {
    const selectedGroup = taskGroups.find((group) => group.id === groupId);
    if (!selectedGroup) return;

    if (allGroupsSelected) {
      changeFilter({ selectedTaskGroups: [selectedGroup], page: 0 });
      return;
    }

    const isSelected = selectedTaskGroups.some((group) => group.id === groupId);
    const nextSelection = isSelected
      ? selectedTaskGroups.filter((group) => group.id !== groupId)
      : [...selectedTaskGroups, selectedGroup];

    // An empty group filter is interpreted by the API as "all groups". Keep
    // that state explicit in the UI instead of showing no active selection.
    changeFilter({ selectedTaskGroups: nextSelection.length > 0 ? nextSelection : [...taskGroups], page: 0 });
  };

  const deleteGroup = async (groupId: string, groupName: string) => {
    if (groupItemCounts[groupId] !== 0 || deletingGroupId) return false;

    setDeletingGroupId(groupId);

    try {
      const removed = await AgendaS.deleteGroup(groupId, groupName);
      if (!removed) return false;

      const remainingGroups = taskGroups.filter((group) => group.id !== groupId);
      const remainingSelection = selectedTaskGroups.filter((group) => group.id !== groupId);

      setGroupItemCounts((counts) => {
        const nextCounts = { ...counts };
        delete nextCounts[groupId];
        return nextCounts;
      });
      changeFilter({
        selectedTaskGroups: remainingSelection.length > 0 ? remainingSelection : remainingGroups,
        page: 0,
      });
      loadGroups();
      return true;
    } finally {
      setDeletingGroupId(undefined);
    }
  };

  const confirmDeleteGroup = (groupId: string, groupName: string) => {
    openDeleteConfirmModal({
      title: 'Aufgabengruppe löschen',
      description: `Möchtest du die Gruppe „${groupName}“ wirklich löschen?`,
      onConfirm: () => deleteGroup(groupId, groupName),
    });
  };

  const onApplicationUsersChange = (values: string[]) => {
    changeFilter({ applicationUsersOfInterests: values.map(Number), page: 0 });
  };

  return (
    <>
      {!sm && (
        <Group p="sm" position="apart" sx={{ alignItems: 'center' }}>
          <ThemeTitle order={3}> {i18n._('common.tasks')}</ThemeTitle>
          <AddTaskIcon />
        </Group>
      )}
      <Space h="md" />
      <ThemeText pl={10}>Datumsbereich</ThemeText>
      <Box p="xs">
        <DateRangePickerWithSelect
          zIndex={100}
          shadow="xl"
          labelSeparator="bis"
          placeholder="Pick dates range"
          value={[selectedDateRange[0], selectedDateRange[1]]}
          onChange={onDateChange}
          clearable={false}
          variant="filled"
          showRanges
        />
      </Box>
      <BookingStateItem
        label="Maximum"
        onClick={() => changeFilter({ selectedDateRange: [dateRangePickerMinimum, dateRangePickerMaximum] })}
        active={useDateRangeItem(dateRangePickerMinimum, dateRangePickerMaximum, selectedDateRange)}
      />
      <BookingStateItem
        label="Heute"
        onClick={() => changeFilter({ selectedDateRange: [dateRangePickerMinimum, lastDateBeforeNextMidnight()] })}
        active={useDateRangeItem(dateRangePickerMinimum, lastDateBeforeNextMidnight(), selectedDateRange)}
      />
      <BookingStateItem
        label="Morgen"
        onClick={() => changeFilter({ selectedDateRange: [dateRangePickerMinimum, addDays(1, startOfDay)] })}
        active={useDateRangeItem(dateRangePickerMinimum, addDays(1, startOfDay), selectedDateRange)}
      />
      <BookingStateItem
        label="In einer Woche"
        onClick={() => changeFilter({ selectedDateRange: [dateRangePickerMinimum, addDays(7, startOfDay)] })}
        active={useDateRangeItem(dateRangePickerMinimum, addDays(7, startOfDay), selectedDateRange)}
      />
      <BookingStateItem
        label="In zwei Woche"
        onClick={() => changeFilter({ selectedDateRange: [dateRangePickerMinimum, addDays(14, startOfDay)] })}
        active={useDateRangeItem(dateRangePickerMinimum, addDays(14, startOfDay), selectedDateRange)}
      />
      <BookingStateItem
        label="In einem Monat"
        onClick={() => changeFilter({ selectedDateRange: [dateRangePickerMinimum, addDays(30, startOfDay)] })}
        active={useDateRangeItem(dateRangePickerMinimum, addDays(30, startOfDay), selectedDateRange)}
      />
      <BookingStateItem
        label="In einem Jahr"
        onClick={() => changeFilter({ selectedDateRange: [dateRangePickerMinimum, addDays(365, startOfDay)] })}
        active={useDateRangeItem(dateRangePickerMinimum, addDays(365, startOfDay), selectedDateRange)}
      />

      <Group position="apart" mt="xl" mb="xs">
        <ThemeText pl={10}>Aufgabengruppen</ThemeText>
      </Group>

      <BookingStateItem label="Alle Gruppen" active={allGroupsSelected} onClick={selectAllGroups} />
      {groupSections.map(
        (section) =>
          section.groups.length > 0 && (
            <Box key={section.label}>
              <MantineText size="xs" weight={600} color="dimmed" mt="md" mb={4} pl={20}>
                {section.label}
              </MantineText>
              {section.groups.map((group) => (
                <BookingStateItem
                  key={group.id}
                  label={group.name}
                  active={!allGroupsSelected && selectedTaskGroups.some((activeGroup) => activeGroup.id === group.id)}
                  onClick={() => toggleGroup(group.id)}
                  sx={{ paddingLeft: 10 }}
                  rightSection={
                    groupItemCounts[group.id] === 0 ? (
                      <Tooltip label="Leere Gruppe löschen" withArrow position="right">
                        <ActionIcon
                          size="sm"
                          color="red"
                          variant="subtle"
                          disabled={Boolean(deletingGroupId)}
                          aria-label={`${group.name} löschen`}
                          onClick={() => confirmDeleteGroup(group.id, group.name)}
                        >
                          <IconX size={14} />
                        </ActionIcon>
                      </Tooltip>
                    ) : undefined
                  }
                />
              ))}
            </Box>
          ),
      )}
      {hasGeneralTasksScope && teamAddonPresent && (
        <>
          <Space h="xl" />
          <ThemeText pl={10}>Zugewiesen an</ThemeText>
          <Box p="xs">
            <MultiSelect
              searchable
              clearable
              data={applicationUserItems}
              value={applicationUsersOfInterests.map((applicationUserId) => applicationUserId.toString())}
              onChange={onApplicationUsersChange}
              icon={<IconUser size={16} />}
              variant="filled"
              placeholder="Alle Personen"
              nothingFound="Keine Personen gefunden"
              disabled={loadingApplicationUsers}
              itemComponent={ApplicationUserFilterItem}
              zIndex={100}
              shadow="xl"
            />
          </Box>
        </>
      )}
      <Space h="sm" />
      <Checkbox
        ml={15}
        label="fertige Aufgaben"
        checked={includeCompleted}
        onChange={(event) => onIncludeCompletedChange(event.currentTarget.checked)}
      />
    </>
  );
};

export default TasksNavFilter;
